/**
 * 전투 하나를 명부와 시드에서 만들어낸다.
 *
 * 지형과 배치 흔들림만 시드가 정하고, 누가 나가는지는 명부가 정한다.
 * 장비와 개체 성장이 붙으면 시드만으로는 부대를 재구성할 수 없기 때문이다.
 * 서버는 이 명부가 계정의 실제 보유분과 맞는지를 따로 검증한다.
 */
import {
  FOG_RANGE_PCT,
  SUPPLY_CUT_PCT,
  FORTRESS_HOLD_TICKS,
  AMBUSH_PCT,
  AMBUSH_TICKS,
  CANYON_WIDTH_PCT,
  NIGHT_RANGE_PCT,
  RAIN_CD_PCT,
  RAIN_RANGE_PCT,
  REINFORCE_EVERY,
  REINFORCE_PCT,
  REINFORCE_WAVES,
  WIND_PCT,
  HEAVY_DEF_BONUS,
  HIGH_GROUND_HOLD_TICKS,
  LAND_MULT,
  PLAINS_WIDTH_PCT,
  Trait,
  WARLORDS,
  fieldedSquads,
  hasTrait,
  sanitizeTraits,
} from './traits.ts';
import type { Trait as TraitId } from './traits.ts';
import { FP_ONE, fp } from './fixed.ts';
import { Rng } from './rng.ts';
import { TILE_FP, TerrainGrid, layFord, plantStakes, raiseHill, raiseWall } from './grid.ts';
import {
  ARROW_SUPPLY,
  RATION_HP_PCT,
  CALTROP_TICKS,
  SHIELD_REPAIR_PCT,
  Supply,
  hasSupply,
  sanitizeSupplies,
} from './supplies.ts';
import type { Supply as SupplyId } from './supplies.ts';
import { World } from './world.ts';
import { Team } from './units.ts';
import { MAX_SQUADS } from './commands.ts';
import { SKILL_CODE, sanitizeSkill, skillParams } from './skills.ts';
import { MATE_SKILL_BY_ID, mateSkillLevel, mateSkillPcts } from './mate-skills.ts';
import { DEPLOY_COL_PX, DEPLOY_SHARE_PX, autoDeploy, deployOffsets, sanitizeDeploy } from './deploy.ts';
import { MAX_UNITS } from './units.ts';
import {
  foeEliteCount,
  foeElitePlan,
  foeHeadSquad,
  foeEliteSpec,
  Branch,
  BranchCode,
  Culture,
  LEADER_BASE,
  NO_CAPTAIN,
  Role,
  addCaptain,
  boostSpec,
  clampLevel,
  commandBonus,
  computePower,
  heroStatsAt,
  banditChiefSpec,
  foeChiefLevel,
  foeSquadsAt,
  subChiefSpec,
  banditRoster,
  chiefSpec,
  companionSpec,
  companionsOf,
  depthRoster,
  leaderSpec,
  STANDARD_LEVEL,
  ELITE,
  eliteBody,
  specForTroop,
  standardRoster,
  TROOP_BY_ID,
} from './troops.ts';
import type { CombatantSpec, CompanionDef, Stats } from './troops.ts';
import {
  applyCommand,
  RIDING_SPEED,
  fieldLoadout,
  isMounted,
  loadoutIds,
  riderStats,
  sanitizeLoadout,
  shieldBig,
  shieldBlocks,
  equipOf,
  itemBase,
  shoots,
} from './equipment.ts';
import type { Loadout } from './equipment.ts';

export interface RosterEntry {
  readonly troop: string;
  readonly count: number;
  /** 소속 분대. 0번은 장수 직할대다. */
  readonly squad?: number;
  /**
   * 계정에 있는 병사의 식별자. 출정 화면에서 고른 실제 병사들이다.
   * 전투가 끝나면 이 값으로 누가 죽었는지 계정에 돌려준다.
   */
  readonly uids?: readonly number[];
  /**
   * 이 줄 병사들의 레벨. 없으면 1이다. 같은 병종이라도 레벨이 다르면 줄을 나눈다.
   *
   * **전투 판정에 들어가는 값이다.** 체력과 공격이 여기서 나오므로 계정이 아니라
   * 명부에 실려야 서버가 같은 전투를 다시 만든다. `companionLevels`와 같은 처지로,
   * 계정의 실제 경험치와 맞는지는 아직 아무도 대조하지 않는다.
   */
  readonly level?: number;
  /**
   * 이 줄 병사들이 앞선 판에서 쌓은 피로(`uids`와 같은 순서, 0~`FATIGUE_MAX`, Todo6). 없으면 0. 명부에 실리는 판정
   * 값이다 — 서버가 같은 몸을 세운다(그 출정의 앞선 판에서 나온 값인지는 서버가 그 판들을 이어 대조한다).
   */
  readonly fatigue?: readonly number[];
  /**
   * 단련(백분율, `boostSpec`). 적 줄에만 뜻이 있다 — 113부터 `depthRoster`는 싣지 않는다. **우리 편 줄에는 쓰지 않는다** —
   * 리플레이 JSON의 명부는 클라이언트가 실어 오므로, 우리 편에 붙이면 자기 병사를 부풀릴 수 있다(`deploy`).
   */
  readonly boost?: number;
  /**
   * 이 줄의 병사는 용사다(`ELITE`, troops.ts). **우리 편 줄에만 쓴다.** 판정에 드는 값이라 명부에 실린다. 자격(최고 티어·
   * 레벨)이 없는 줄에는 붙어도 먹지 않고, 판 전체에서 `ELITE.max`명까지만 용사의 몸을 준다 — 명부는 클라이언트가 실어
   * 오므로 문 앞에서 거른다. 계정에서 정말 임명한 병사인지는 레벨처럼 아직 대조하지 않는다.
   */
  readonly elite?: true;
}


export interface ScenarioConfig {
  readonly seed: number;
  readonly cols: number;
  readonly rows: number;
  /** 출정 깊이. 1부터 시작해 전투마다 오른다. */
  readonly depth: number;
  readonly playerCulture: Culture;
  readonly enemyCulture: Culture;
  /** 플레이어가 데려온 병력. 영구 사망 대상이다. */
  readonly roster: readonly RosterEntry[];
  /**
   * 분대별 동료. 색인이 곧 분대 번호다. 0번은 장수가 맡으므로 비어 있다.
   *
   * 예전에는 식별자 목록만 받아 1번 분대부터 순서대로 배정했다. 그러면 가운데
   * 분대를 비우거나 동료가 쓰러져 목록이 줄어들 때 남은 동료가 다른 분대로
   * 옮겨가 버린다. 출정 화면이 보여준 지휘 보정이 엉뚱한 병종에 붙는다.
   */
  readonly companions?: readonly (string | null)[];
  /**
   * 분대별 착장. 색인이 곧 분대 번호다. 0번은 장수 것이다.
   *
   * `companions`와 같은 모양으로 둔 이유가 있다. 0번 분대는 장수가 맡으므로
   * `companions[0]`이 비어 있고, 그 자리를 장수 착장이 쓴다. 두 배열의 색인이
   * 같은 것을 가리켜야 화면이 보여준 보정이 그 분대에 그대로 붙는다.
   */
  readonly loadouts?: readonly (Loadout | null)[];
  /**
   * 분대별 동료의 레벨(1~99). 색인이 곧 분대 번호다. 없으면 1. 옛 이름은 `companionGrades`(단계 0~3)였다 —
   * 동료가 장수와 같은 길로 크게 되며 레벨로 바꿨다(`companionSpec`).
   *
   * **전투 판정에 들어가는 값이다.** 레벨이 동료의 몸과 지휘 보정을 올리고 보정은 분대의
   * 타격과 사기를 바꾼다. 그래서 계정이 아니라 명부에 실려야 한다 — 서버가 든
   * 것은 이 `cfg`뿐이고, 계정 쪽 숫자를 읽으면 같은 전투를 다시 못 만든다.
   * 명부에 적힌 레벨이 실제 계정의 것인지는 **아직 아무도 대조하지 않는다.**
   * `roster`·`loadouts`·`companions`와 같은 처지다 — cfg 전체가 클라이언트의
   * 주장이고, 제출 시 계정과 맞춰 보는 층이 없다. 랭킹을 열기 전에 반드시
   * 필요하다. 무엇을 대조해야 하는지는 `docs/design.md`에 적어 두었다.
   */
  readonly companionLevels?: readonly number[];
  /**
   * 전장 특성(traits.ts). 적 편성·지형·방어·전과를 바꾸는 **판정 값**이라 명부에 실린다. 출정 시드와 깊이가 정하지만
   * (`traitsForDepth`) 리플레이는 아무 목록이나 실어 오므로 문 앞에서 거른다(`battleTraits`). 없으면 특성 없는 판.
   */
  readonly traits?: readonly TraitId[];
  /**
   * 갈림길에서 고른 길(`Route`, 6단계). 판에는 쓰지 않는다 — 땅은 이미 `traits`에 실렸다. 검증(`verifyReplay`)이
   * 갈림길 구간에서 이 길로 특성을 다시 내어 맞춰 본다. 다른 깊이에서는 비운다.
   */
  readonly route?: string;
  /**
   * 장수 곁(0번 분대)에서 싸우는 동료의 분대 자리(1~3). 없으면 -1이거나 비운다. 그 동료는 전장에서 0번 분대에 서고
   * 지휘하지 않으며, 지휘 보정(레벨·착장 몫)은 장수의 보정에 더해져 내 부대에 걸린다(합은 `CAPTAIN_CAP`). 제 분대는
   * 지휘관 없이 서므로 병사가 장수를 따른다. **전투 판정에 들어가는 값**이라 명부에 싣는다.
   */
  readonly escortSquad?: number;
  /**
   * 분대별 출전 위치(`sim/deploy.ts`의 칸 번호 0~5, 색인이 분대 번호). 있으면 분대가 고른 칸에 모여 서고 대형도 그 칸을
   * 지킨다. 칸 번호가 아니거나 빠진 분대는 기본 배치(`autoDeploy`)다. **없으면 옛 방식** — 병사가 분대 구분 없이 한
   * 덩어리에 섞여 서고 대형이 병종으로 자리를 고른다(옛 명부·도구가 그대로 재현된다). 첫 틱의 자리와 대형의 자리를
   * 바꾸는 **판정 값**이라 명부에 싣는다.
   */
  readonly deploy?: readonly number[];
  /**
   * 장수 기술(`sim/skills.ts`, Todo10)과 그 레벨. 단추 하나에 든 한 가지. 없으면 함성 레벨 1 — 예전 판이다. 판정 값이라
   * 명부에 싣고 문 앞에서 거른다(`sanitizeSkill`). 그 레벨이 장수 레벨의 기술 점수 안인지는 서버의 계정 대조가 본다.
   */
  readonly skill?: { readonly id: string; readonly level: number };
  /**
   * 장수의 레벨. 없으면 1이다. 장수의 몸과, 모든 분대에 걸리는 지휘 보정이 여기서
   * 나온다(`leaderSpec`, `commandBonus`). 동료 레벨과 같은 까닭으로 명부에 싣는다.
   */
  readonly heroLevel?: number;
  /**
   * 이 출정에 처음 데려간 병사 수(`RunState.deployed`) — 생환 배율의 분모다. 전과(`meritScore`)는 판의 명부가 아니라
   * **출정 전체**의 생환으로 매긴다(`applyBattle`). 명부만 보고 세면 둘째 판부터(명부가 줄었다), 그리고 좁은 협곡에서
   * 두 분대만 나간 판에서 서버(`summarize`)와 클라이언트의 전과가 갈렸다(리뷰가 짚었다). 없으면 이 판의 명부 수.
   * 그 출정에 처음 데려간 전체 편성 수(첫 판이 협곡이면 그 판 명부 + `resting`)와 같은지는 서버의 계정 대조가 본다
   * (`docs/design.md`).
   */
  readonly runDeployed?: number;
  /**
   * 이 판을 쉰 병사 수(좁은 협곡에서 데려가지 않은 분대, `RunState.fielded`). 전장에 없었으므로 살아 있고, 생환에 든다.
   * 없으면 0. 협곡이 아닌 판에서는 0이어야 한다(`verifyReplay`). 그 출정에 남은 병사 중 이 명부에 없는 수와 같은지는
   * 서버의 계정 대조가 본다 — 클라이언트가 적은 수만 믿으면 생환을 부풀린다.
   */
  readonly resting?: number;
  /**
   * 이 판에 쓰는 군수품(supplies.ts). 우리 궁수·창·전열과 적 기병을 바꾸는 **판정 값**이라 명부에 싣고 문 앞에서
   * 거른다(`battleSupplies`). 없으면 쓰지 않은 판.
   */
  readonly supplies?: readonly SupplyId[];
  /**
   * 도적 토벌인가. 장수 혼자 나가는 싸움이다.
   *
   * 적을 깊이가 아니라 고정된 도적떼로 바꾼다. 부대를 전부 잃었을 때 돈을
   * 벌어올 유일한 길이므로, 나갈 때마다 난이도가 달라지면 안 된다.
   */
  readonly raid?: boolean;
  /**
   * 토벌에서 고른 도적 무리(`RAID_BANDS`의 색인, troops.ts). 없거나 모르는 값이면 가장 작은 무리(예전의 하나뿐이던 무리)다
   * — 122 전의 리플레이는 이 값이 없다. 그 무리를 열 관등인지는 계정의 일이라 서버가 따로 본다.
   */
  readonly raidBand?: number;
}

/**
 * 걷는 영웅(장수·동료)에게 착장을 입힌다. 말을 찼으면 기병, 걸으며 활을 들었으면 궁수, 아니면 걷는 보병이다.
 *
 * 말은 가지(기병/보병)와 속도를 통째로 바꾼다 — 말 위에서 창에 2.6배로 맞고, 숲에서
 * 느려지고, 돌격이 붙는다. 능력치는 `riderStats`가 정하므로 병기고 화면과 전장이 같은
 * 몸을 본다.
 *
 * 능력치는 본인에게, 지휘 보정은 휘하 분대에 붙는다. 영웅은 사기가 최대라 개인
 * 사기 보정이 죽은 값이기 때문이다. 이름난 칼이 주는 것은 본인의 담력이 아니라
 * 그걸 보는 병사들의 담력이다.
 *
 * 처음에는 장수만 이 길로 입혔고, 동료는 능력치만 바꾸는 따로 된 문(`dressed`)을
 * 거쳐 말이 없어도 기병이었다. 동료도 말이 없으면 걷게 되면서 한 문으로 모았다.
 */
function mountUp(foot: CombatantSpec, worn: Loadout, mountedPower: number): CombatantSpec {
  const mounted = isMounted(worn);
  // 말 위의 활은 쓰지 않는 것으로 친다(`fieldLoadout`) — 지휘 보정과 그림도 능력치(`riderStats`)와 같은 착장을 본다.
  const used = fieldLoadout(worn);
  const ids = loadoutIds(used);
  return {
    ...foot,
    // 걷는 사람이 활을 들면 궁수의 가지다(`shoots`): 전장의 "누가 쏘는가"는 가지가 정하므로(화살통·활 놓기·겨누기·
    // 곡사·날씨·적 기병의 사냥감), 가지를 주면 병사 궁수와 같은 규칙을 그대로 탄다. 장수만의 것은 둘이다(`World`):
    // 몰고 있는 동안은 쏘지 않고, 화살을 다 써도 전장을 떠나지 않는다.
    branch: mounted ? BranchCode.Horse : shoots(worn) ? BranchCode.Bow : BranchCode.Blade,
    // 격(power)은 착장으로 바꾸지 않는다. 예전에도 장비를 입혀도 격은 그대로였다.
    // 영웅이 쓰러질 때 상대의 처치 격에 이 값이 더해진다. 처음에는 착장 능력치로 다시
    // 셈해 말을 탄 장수의 격이 519에서 527~549로 바뀌었다 — "말을 탄 장수는 예전
    // 그대로"가 깨졌다고 리뷰가 잡았다. 말을 타면 옛 몸(걷는 몸 + 올라탄 몫)의 격,
    // 걸으면 걷는 몸의 격이다.
    power: mounted ? mountedPower : foot.power,
    stats: riderStats(foot.stats, worn),
    // 지휘 보정이 없으면 비워 둔다. 상한은 World.setCaptain이 건다 — 값이 들어가는
    // 문 하나에서만 거는 편이 안전하다.
    captain: ids.some((id) => id !== undefined) ? applyCommand(foot.captain ?? NO_CAPTAIN, ids) : foot.captain,
    // 방패 칸의 방패(`shieldBlocks`). 병사의 방패와 같은 규칙으로 화살을 막고 부서진다.
    shield: shieldBlocks(worn),
    shieldBig: shieldBig(worn),
    // 그림은 바탕 물건으로 찾는다 — 강화한 열쇠(`w_x+2`)를 그대로 넘기면 층 이름이 `weapon_x+2`가 되어 맨손 칼로 그렸다.
    gear: {
      weapon: used.weapon && itemBase(used.weapon),
      armor: worn.armor && itemBase(worn.armor),
      helm: worn.helm && itemBase(worn.helm),
      mount: worn.mount && itemBase(worn.mount),
    },
  };
}

/** 말에 오른 몸의 격. 기병으로 셈한다. */
function mountedPower(body: Stats): number {
  return computePower({ ...body, speed: body.speed + RIDING_SPEED }, Branch.Horse);
}

/**
 * 전장에 서는 장수. 말을 찼으면 기병, 걸으며 활을 들었으면 궁수, 아니면 걷는 보병이다.
 *
 * 말을 탄 레벨 1 맨몸 장수의 격은 예전 장수(속도 112 기병)의 격과 같다. 레벨이
 * 오르면 자란 몸으로 다시 잰다.
 */
export function dressLeader(
  culture: Culture,
  loadout: Loadout | null | undefined,
  level = 1,
): CombatantSpec {
  const foot = leaderSpec(culture, level);
  return mountUp(foot, sanitizeLoadout(loadout, culture), mountedPower(heroStatsAt(LEADER_BASE, level)));
}

/**
 * 장수의 지휘를 동료가 맡은 분대에 얹는다. 동료 보정 + 장수 지휘 (+ 동료 장비는
 * 이미 `mountUp`이 얹었다). 상한은 `World.setCaptain`이 건다.
 *
 * 능력치 화면(`ui/stats.ts`)도 이것으로 동료 분대의 보정을 보인다. 화면이 따로 더하면
 * 장비 몫을 빠뜨렸다(리뷰가 짚었다 — 삼엽환두대도의 사기 +10이 카드에 없었다).
 */
export function underCommand(spec: CombatantSpec, heroLevel: number): CombatantSpec {
  const cmd = commandBonus(heroLevel);
  if (cmd === NO_CAPTAIN) return spec;
  return { ...spec, captain: addCaptain(spec.captain ?? NO_CAPTAIN, cmd) };
}

/**
 * 전장에 서는 동료. 말을 찼으면 기병, 걸으며 활을 들었으면 궁수, 아니면 걷는 보병이다. 말을 탄 레벨 1 동료의
 * 격과 능력치는 예전(늘 기병이던 때)과 같다. 레벨이 오르면 장수처럼 자란 몸으로 다시 잰다.
 */
export function dressCompanion(
  def: CompanionDef,
  level: number,
  loadout: Loadout | null | undefined,
  culture: Culture,
): CombatantSpec {
  const spec = companionSpec(def, level);
  return mountUp(spec, sanitizeLoadout(loadout, culture), mountedPower(spec.stats));
}

/**
 * 말에서 내린 몸(전장 특성 `Trait.MountainPath` — "말을 탈 수 없다"). 기병 병사는 같은 나라·같은 티어 칼의 걸음과
 * 몸집으로 걷고, 체력은 말의 몫을 뺀다(칼 체력 / 기병 체력의 비로 줄인다 — 기병의 체력에는 말이 들어 있다). 공격·방어는
 * 사람의 것이라 그대로다. 가지가 칼이 되어 돌격이 없고 창에 약하지도 않다. 적장은 말 탄 몫(`RIDING_SPEED`)만큼 느려진다.
 * 격은 걷는 몸으로 다시 잰다 — 처치 격(전과)이 말 탄 몸의 값이면 약해진 적을 베고 말 탄 적을 벤 값을 받는다.
 * 장수·동료는 착장에서 말을 빼고 입힌다(`createBattle`).
 */
export function dismount(spec: CombatantSpec): CombatantSpec {
  if (spec.branch !== BranchCode.Horse) return spec;
  if (spec.role === Role.Soldier) {
    const horse = TROOP_BY_ID.get(spec.troop);
    const foot = TROOP_BY_ID.get(spec.troop.replace('_horse', '_blade'));
    if (!horse || !foot) return spec;
    const stats: Stats = {
      ...spec.stats,
      hp: Math.max(1, Math.floor((spec.stats.hp * foot.hp) / horse.hp)),
      speed: foot.speed,
      radius: foot.radius,
      mass: foot.mass,
    };
    return { ...spec, branch: BranchCode.Blade, stats, power: computePower(stats, Branch.Blade), dismounted: true };
  }
  // 적장도 걷는 몸으로 격을 다시 잰다. 처음에는 속도만 바꿔, 내린 적장을 베고 말 탄 적장의 격(317 → 285)을 받았다
  // (리뷰가 짚었다).
  const stats: Stats = { ...spec.stats, speed: Math.max(1, spec.stats.speed - RIDING_SPEED) };
  return { ...spec, branch: BranchCode.Blade, stats, power: computePower(stats, Branch.Blade), dismounted: true };
}

/**
 * 전황이 궁수에게 거는 몸(전장 특성 3단계): 야습(`Trait.Night` — 사거리 `NIGHT_RANGE_PCT`%, 횃불이면 우리는 그대로),
 * 비(`Trait.Rain` — 사거리 `RAIN_RANGE_PCT`%, 장전 `RAIN_CD_PCT`%)와 바람
 * (순풍·역풍 — 사거리 ±`WIND_PCT`%). 스펙에 거는 까닭은 나중에 나타나는 적(매복·원군, `queueSpawn`)도 같은 몸이어야
 * 해서다 — 배치한 뒤 배열을 고치면 그 뒤에 선 적은 빠진다. 기름 먹인 시위(군수품)면 우리 궁수는 비를 타지 않는다.
 */
function weathered(
  spec: CombatantSpec,
  team: Team,
  traits: readonly TraitId[],
  supplies: readonly SupplyId[],
  proof: readonly string[] = [],
): CombatantSpec {
  if (spec.branch !== BranchCode.Bow) return spec;
  // 분대 지휘관의 특성 짝 장비(`EquipDef.weatherproof`)가 지운 날씨는 건너뛴다.
  traits = traits.filter((t) => !proof.includes(t));
  let range = spec.stats.range;
  let cd = spec.stats.cd;
  const mine = team === Team.Player;
  if (hasTrait(traits, Trait.Night) && !(mine && hasSupply(supplies, Supply.Torch))) {
    range = Math.floor((range * NIGHT_RANGE_PCT) / 100);
  }
  if (hasTrait(traits, Trait.Fog)) range = Math.floor((range * FOG_RANGE_PCT) / 100);
  if (hasTrait(traits, Trait.Rain) && !(mine && hasSupply(supplies, Supply.OiledString))) {
    range = Math.floor((range * RAIN_RANGE_PCT) / 100);
    cd = Math.floor((cd * RAIN_CD_PCT) / 100);
  }
  const wind = hasTrait(traits, Trait.Tailwind) ? 1 : hasTrait(traits, Trait.Headwind) ? -1 : 0;
  if (wind !== 0) {
    const favoured = (wind > 0) === mine;
    range = Math.floor((range * (favoured ? 100 + WIND_PCT : 100 - WIND_PCT)) / 100);
  }
  if (range === spec.stats.range && cd === spec.stats.cd) return spec;
  return { ...spec, stats: { ...spec.stats, range, cd } };
}

/** 말을 뺀 착장(산길). 장수·동료가 말 없이 입으면 걷는 보병이고, 활을 들었으면 궁수다 — 말 위에서 못 쓰던 활이 살아난다(`mountUp`). */
function withoutMount(l: Loadout | null | undefined): Loadout | null | undefined {
  if (!l || l.mount === undefined) return l;
  const { mount: _mount, ...rest } = l;
  return rest;
}

function deploy(
  world: World,
  rng: Rng,
  roster: readonly RosterEntry[],
  team: Team,
  cx: number,
  cy: number,
  spreadX: number,
  spreadY: number,
  uidBase: number,
  body: (s: CombatantSpec, squad: number) => CombatantSpec = (s) => s,
  // 분대마다 [중심 x, 중심 y, 가로 흩어짐, 세로 흩어짐](출전 위치). 없으면 모두 (cx, cy)에 섞여 선다.
  around?: (squad: number) => readonly [number, number, number, number],
): number {
  let uid = uidBase;
  const spot = new Int32Array(2);
  // 용사는 판 전체에서 `ELITE.max`명까지(명부 순서대로).
  let elitesLeft: number = ELITE.max;
  for (let row = 0; row < roster.length; row++) {
    const entry = roster[row];
    // 레벨은 문 앞에서 거른다(`clampLevel`). 리플레이 JSON은 아무 값이나 실어 온다.
    // 아군 병사는 정병이다(`retinueStats`). 적병은 징발된 그대로다.
    const lv = clampLevel(entry.level);
    const mine = team === Team.Player;
    const squad = entry.squad ?? 0;
    // 단련은 적에게만(`RosterEntry.boost`).
    const spec: CombatantSpec = body(mine ? specForTroop(entry.troop, lv, mine) : boostSpec(specForTroop(entry.troop, lv, false), entry.boost), squad);
    const [ax, ay, asx, asy] = around ? around(squad) : [cx, cy, spreadX, spreadY];
    for (let k = 0; k < entry.count; k++) {
      // 난수는 자리마다 정확히 두 번. 바위 여부와 무관하게 같은 횟수여야 한다.
      const x = ax + rng.range(-asx, asx);
      const y = ay + rng.range(-asy, asy);
      world.freeSpot(x, y, spot);
      const id = entry.uids ? (entry.uids[k] ?? 0) : uid++;
      // 계정 병사는 레벨마다 굴린 몫이 저마다 다르다(`soldierGrowth`). 몸은 병사마다 World에 복사되므로
      // 스펙 표(`World.specIndex`)가 병종·레벨로 하나로 묶여도 병사별 체력·공격은 따로 선다.
      const elite = mine && entry.elite === true && elitesLeft > 0 && eliteBody(entry.troop, lv);
      if (elite) elitesLeft--;
      const at = world.spawn(
        mine && (entry.uids || elite) ? body(specForTroop(entry.troop, lv, true, entry.uids ? id : undefined, elite), squad) : spec,
        team,
        spot[0],
        spot[1],
        id,
        squad,
      );
      // 앞선 판의 피로(`RosterEntry.fatigue`). 우리 병사만 싣는다.
      if (mine && entry.fatigue && at >= 0) world.setFatigue(at, entry.fatigue[k] ?? 0);
      // 용사는 무조건 호위한다: 제가 든 부대의 대장을(`World.bindDuties`). 명부가 따로 실어 오는 것은 없다 — 줄의
      // `squad`가 곧 지킬 사람이다(옛 `ward`는 REPLAY 100에서 없앴다). 적의 용사는 부대를 나눈 뒤에 올린다(`createBattle`).
      if (elite && at >= 0) world.setDuty(at);
    }
  }
  return uid;
}

/**
 * 전장 정원을 넘는 명부는 거절한다.
 *
 * `World.spawn`은 꽉 차면 조용히 실패한다. 스폰 순서가 아군 → 장수 → 동료 →
 * 적 → 적장이라 넘치면 적이 잘리고 **적장이 제일 먼저 사라진다.** 터지지도
 * 않고 결정론도 유지되므로, 깊이 50을 재 보고 있다고 믿는 동안 실제로는
 * 적장 없는 깊이 46을 보게 된다.
 */
/** 출전 위치에서 분대 하나가 서는 흩어짐(px): 병사 수에 따라. 정수 계단 — 시뮬레이션에 부동소수점을 쓰지 않는다. */
function squadSpreadPx(n: number): number {
  return n <= 12 ? 70 : n <= 30 ? 100 : n <= 60 ? 130 : 160;
}

/**
 * 출전 위치(`ScenarioConfig.deploy`)를 전장 자리로. 분대마다 칸 안의 자리(`deployOffsets`)를 장수 분대 칸에서 본 상대
 * 자리(`post`, 대형의 자리)와, 병사가 흩어져 서는 중심·폭(`around`)으로 낸다. 서지 않는 분대는 장수 칸에 선다(장수 곁
 * 분대의 병사는 장수를 따른다). 좁은 전장(협곡)에서는 옆 거리를 벽 안쪽으로 줄인다.
 */
function deployPosts(
  raw: readonly number[],
  roster: readonly RosterEntry[],
  leads: (sq: number) => boolean,
  escortSquad: number,
  midX: number,
  spreadX: number,
): { post: ([number, number] | null)[]; around: (sq: number, baseY: number) => readonly [number, number, number, number] } {
  const counts = new Array<number>(MAX_SQUADS).fill(0);
  const troops: { troop: string; count: number }[][] = Array.from({ length: MAX_SQUADS }, () => []);
  for (const e of roster) {
    const sq = e.squad ?? 0;
    if (!Number.isInteger(sq) || sq < 0 || sq >= MAX_SQUADS || !(e.count > 0)) continue;
    counts[sq] += e.count;
    troops[sq].push({ troop: e.troop, count: e.count });
  }
  // 서는 분대: 장수 분대, 병사가 있거나 동료가 지휘하는 분대. 장수 곁 분대는 장수와 함께 선다(제 칸이 없다).
  const stands = (sq: number): boolean => sq === 0 || (sq !== escortSquad && (counts[sq] > 0 || leads(sq)));
  const auto = autoDeploy((sq) => (stands(sq) ? troops[sq] : null));
  const offs = deployOffsets(sanitizeDeploy(raw, auto));
  // 옆 거리: 가장 바깥 분대가 벽에서 80px 안쪽에 들게. 옛 전열 폭(`spreadX`)이 협곡에서 줄어드는 것과 같은 생각.
  const widest = fp(DEPLOY_COL_PX + DEPLOY_SHARE_PX);
  const room = Math.max(0, Math.min(widest, spreadX));
  const side = (px: number): number => Math.floor((fp(px) * room) / widest);
  const [lx, ly] = offs[0] ?? [0, 0];
  const post = offs.map((o, sq) => (sq === 0 || !o ? null : ([side(o[0]) - side(lx), fp(o[1] - ly)] as [number, number])));
  return {
    post,
    around: (sq, baseY) => {
      const o = offs[sq] ?? offs[0] ?? [0, 0];
      const half = fp(squadSpreadPx(sq === 0 ? counts[0] + (escortSquad > 0 ? counts[escortSquad] : 0) : counts[sq]));
      return [midX + side(o[0]), baseY - fp(o[1]), Math.min(half, spreadX), half >> 1];
    },
  };
}

/** 이 판에서 맞설 적. 토벌이면 도적떼, 아니면 그 깊이의 정규군이다. */
function enemyRoster(cfg: ScenarioConfig): RosterEntry[] {
  return cfg.raid ? banditRoster(cfg.raidBand) : depthRoster(cfg.enemyCulture, cfg.depth, battleTraits(cfg));
}

/** 이 판에 쓰는 군수품(`sanitizeSupplies`). 토벌에는 없다 — 장수 혼자의 싸움이다. */
export function battleSupplies(cfg: ScenarioConfig): SupplyId[] {
  return cfg.raid ? [] : sanitizeSupplies(cfg.supplies);
}

/** 이 판의 전장 특성(`sanitizeTraits`). 토벌에는 없다. */
export function battleTraits(cfg: ScenarioConfig): Trait[] {
  return cfg.raid ? [] : sanitizeTraits(cfg.traits);
}

/** 원군 한 번에 오는 줄(`REINFORCE_PCT`%). 처음 편성을 줄마다 깎는다 — 줄이 작아 0이면 그 병종은 오지 않는다. */
function reinforceWave(roster: readonly RosterEntry[]): RosterEntry[] {
  return roster
    .map((e) => ({ ...e, count: Math.floor((e.count * REINFORCE_PCT) / 100) }))
    .filter((e) => e.count > 0);
}

/** 매복으로 더 오는 줄(`AMBUSH_PCT`%). */
function ambushBand(roster: readonly RosterEntry[]): RosterEntry[] {
  return roster
    .map((e) => ({ ...e, count: Math.floor((e.count * AMBUSH_PCT) / 100) }))
    .filter((e) => e.count > 0);
}

/**
 * 판 도중에 더 올 수 있는 적(매복·원군). 결과 화면의 예고가 이것으로 적는다 — 처음 편성만 적으면 원군 판의 "적 70명"이
 * 실제로는 86명까지 됐다(리뷰가 짚었다). 원군은 그 전에 이기면 오지 않으므로 최대치다.
 */
export function lateFoes(roster: readonly RosterEntry[], traits: readonly TraitId[]): { ambush: number; reinforce: number } {
  const sum = (list: readonly RosterEntry[]) => list.reduce((n, e) => n + e.count, 0);
  return {
    ambush: hasTrait(traits, Trait.Ambush) ? sum(ambushBand(roster)) : 0,
    reinforce: hasTrait(traits, Trait.Reinforce) ? REINFORCE_WAVES * sum(reinforceWave(roster)) : 0,
  };
}

function assertFits(cfg: ScenarioConfig): void {
  const mine = cfg.roster.reduce((n, e) => n + e.count, 0);
  const foes = enemyRoster(cfg);
  // 매복·원군도 전장 정원에 든다 — 넘으면 `spawn`이 조용히 실패한다.
  const traits = battleTraits(cfg);
  const late = lateFoes(foes, traits);
  const extra = late.ambush + late.reinforce;
  // 적의 대장들(총대장 + 부장, 적장 결전은 적장 셋 + 부장, 토벌은 두령 하나)도 정원에 든다 — 전장이 세우는 그 수다(`foeSquadsAt`).
  // 두 번 빼먹었다: 적장 결전의 둘을 빼먹어 꽉 찬 명부에서 둘이 조용히 못 서고 남은 하나가 "마지막 적장"이 됐고, 부장을
  // 빼먹어 맨 나중에 서는 총대장이 못 섰다(리뷰가 재현했다 — `chiefIdx`가 부장을 가리켰다).
  const commanders = cfg.raid ? 1 : foeSquadsAt(cfg.enemyCulture, cfg.depth, traits);
  const theirs = foes.reduce((n, e) => n + e.count, 0) + extra + commanders;
  // 장수 하나, 동료 최대 MAX_SQUADS-1.
  const total = mine + theirs + 1 + (MAX_SQUADS - 1);
  if (total > MAX_UNITS) {
    throw new Error(
      `전장 정원을 넘습니다: 아군 ${mine} + 적 ${theirs} = ${total} > ${MAX_UNITS} (깊이 ${cfg.depth})`,
    );
  }
}

/**
 * 전장의 크기(칸, 한 칸 32px). 넓은 평원·좁은 협곡은 가로만 바꾼다. 양군은 위아래 가장자리에서 170px 안쪽에 서므로
 * 세로가 곧 출발 거리다.
 *
 * 세로는 32칸이었다(출발 거리 684px). 맞붙기까지 4~6초, 보통 판 16~27초였다 — 기획의 한 판 길이에 한참 못 미쳤다.
 * 걸음 85%(`PACE_PCT`)에 세로를 40·48·52·56으로 재어(`npm run length`, 시드 30개) 56을 골랐다: 맞붙기 전 10~12초,
 * 보통 판 30~35초(동료 셋의 벽 앞 깊이는 50초대). 48은 맞붙기 전이 8~10초로 모자랐다. 고를 때는 특성 없는 맨 판으로
 * 쟀다 — 실제 출정의 특성을 붙인 마지막 값(관문 45~61초 등)은 docs/design.md "전투 길이"에 있다. 피해를 낮춰
 * 늘리지 않는다 — 치고받는 맛이 무뎌진다.
 *
 * **32칸으로 되돌렸다(Todo11).** 플레이한 사람이 정했다: "예전에 전투 시간 좀 늘린다고 진영 사이 거리를 벌렸는데 이건
 * 취소하자. 이동 시간이 기니까 지루하다. 대신 모든 병사의 HP를 20프로 증가시키자. 싸우는 모습을 좀 볼 수 있게."
 * 걸음(`PACE_PCT` 85%)은 그대로 두었다 — 청한 것은 거리다. 맞붙기 전이 10~13초에서 6~8초가 됐다(`npm run length`).
 * 판의 길이는 걷는 시간이 아니라 싸우는 시간으로 낸다(`SOLDIER_HP_PCT`).
 *
 * 토벌의 크기(`RAID_ROWS`)는 따로 적어 둔다 — 지금은 출정과 같지만 뜻이 다르다(장수 혼자 나가는 판이라 볼 배치도 내릴
 * 명령도 없다).
 */
export const FIELD_COLS = 44;
export const FIELD_ROWS = 32;
export const RAID_ROWS = 32;

/** 적 전열의 자리(위에서부터)와 가로 흩어짐. 적의 고지 언덕이 이 둘레에 깔린다. */
const ENEMY_Y = fp(170);
const ENEMY_SPREAD_X = fp(300);
/**
 * 적 병사가 처음 서는 가로 흩어짐(횡대로 오는 판 — `World.lineMarch`). 적은 선 폭 그대로 내려오므로(`LINE_RELEASE`) 선 폭이
 * 곧 부딪치는 폭이다. 옛 폭(`ENEMY_SPREAD_X`, 10~90% 구간 470px)은 오는 길에 좁아지는 것을 셈한 값이라, 그대로 두면 기본
 * 배치의 아군(300px)을 양옆에서 감쌌다 — 관문에 들어서는 부대의 제 관문 이김(`npm run stairs`, 관문 5·10·15·20·25)이
 * 30판에 30·28·20·23·29 → 18·28·5·13·17. ±200px(구간 320px)이면 21·28·15·15·26이다.
 *
 * 병사가 많으면 넓힌다 — 한 사람에 20px 칸 하나(세로 흩어짐 ±70px)가 돌아가게. 203명부터 옛 폭이다.
 */
const FOE_LINE_X = fp(200);
const FOE_LINE_PER_SOLDIER = Math.floor((fp(10)) / 7);

export function createBattle(cfg: ScenarioConfig): World {
  assertFits(cfg);
  const traits = battleTraits(cfg);
  const terrainRng = new Rng(cfg.seed ^ 0x5eed1234);
  const plains = hasTrait(traits, Trait.Plains);
  const onFoot = hasTrait(traits, Trait.MountainPath);
  const canyon = hasTrait(traits, Trait.Canyon);
  // 산림·늪지는 그 지형을 여러 배로 깐다(`LAND_MULT`). 넓은 평원은 폭을 늘리고 바위·숲을 뺀다. 좁은 협곡은 폭을 줄인다.
  const cols = plains
    ? Math.floor((cfg.cols * PLAINS_WIDTH_PCT) / 100)
    : canyon
      ? Math.floor((cfg.cols * CANYON_WIDTH_PCT) / 100)
      : cfg.cols;
  const grid = TerrainGrid.generate(cols, cfg.rows, terrainRng, {
    forest: plains ? 0 : hasTrait(traits, Trait.Forest) ? LAND_MULT : 1,
    marsh: hasTrait(traits, Trait.Marsh) ? LAND_MULT : 1,
    rocks: plains ? 0 : 1,
  });
  if (hasTrait(traits, Trait.Ford)) layFord(grid);
  // 적의 고지: 적이 서는 자리(`ENEMY_Y` 둘레, 아래)에 언덕을 깔고, 적은 거기서 기다린다(`World.enemyHold`). 언덕은 적
  // 전열(`ENEMY_SPREAD_X`)보다 조금 넓게 — 옆으로 돌아 오르는 길이 언덕 끝에 남도록 전장 폭 전체를 덮지는 않는다. 흐름장이
  // 지형을 읽기 전(`new World`)에 깐다.
  // 성문(관문 10): 적 전열 앞을 성벽 한 줄이 가로막고 가운데·양쪽에 두 칸 문 셋. 적은 성 안에서 기다린다.
  const fortress = hasTrait(traits, Trait.Fortress);
  if (fortress) {
    const mid = Math.floor(grid.cols / 2);
    const quarter = Math.max(3, Math.floor(grid.cols / 4));
    raiseWall(grid, Math.floor((ENEMY_Y + fp(70) + fp(60)) / TILE_FP), [mid - quarter, mid, mid + quarter], 2);
  }
  const highGround = hasTrait(traits, Trait.HighGround);
  if (highGround) {
    const half = Math.floor((ENEMY_SPREAD_X + fp(64)) / TILE_FP);
    const mid = Math.floor(grid.cols / 2);
    raiseHill(grid, mid - half, 1, mid + half, Math.floor((ENEMY_Y + fp(70) + fp(48)) / TILE_FP));
  }
  const supplies = battleSupplies(cfg);
  // 거마(군수품 `Supply.Stakes`, 수비전 `Trait.Siege`): 우리 전열 앞에 두 줄. 흐름장이 지형을 읽기 전에 깐다.
  if (hasSupply(supplies, Supply.Stakes) || hasTrait(traits, Trait.Siege)) {
    const mid = Math.floor(grid.cols / 2);
    const half = Math.min(Math.floor((ENEMY_SPREAD_X + fp(64)) / TILE_FP), mid - 2);
    const front = Math.floor((grid.heightPx * FP_ONE - fp(170) - fp(70) - fp(40)) / TILE_FP);
    plantStakes(grid, mid - half, mid + half, [front, front - 1]);
  }
  const world = new World(grid, cfg.seed);
  // 화면이 적 대장의 이름을 깊이로 가른다(`World.depth`). 판정에는 쓰이지 않는다.
  world.depth = Number.isFinite(cfg.depth) ? Math.max(0, Math.floor(cfg.depth)) : 0;
  const rng = new Rng(cfg.seed ^ 0x0a11ed);
  const skill = sanitizeSkill(cfg.skill);
  world.setSkill(SKILL_CODE[skill.id], skill.level, skillParams(skill.id, skill.level));

  const wFp = grid.widthPx * FP_ONE;
  const hFp = grid.heightPx * FP_ONE;
  const midX = Math.floor(wFp / 2);

  // 아군은 아래쪽, 적군은 위쪽에서 시작한다. 중앙 회랑에서 만난다.
  const playerY = hFp - fp(170);
  const enemyY = ENEMY_Y;
  // 전열이 화면 폭과 비슷하게 펼쳐지도록 잡는다.
  // 너무 좁으면 기둥이 되어 측면이라는 개념이 사라지고, 너무 넓으면 서로 만나지 못한다.
  // 좁은 협곡에서는 벽 안쪽에 들어오도록 줄인다. 벽에 붙여 세우면 `freeSpot`이 한쪽으로 몰아 전열이 기울었다.
  const spreadX = Math.min(ENEMY_SPREAD_X, midX - fp(80));
  const spreadY = fp(70);

  if (highGround) world.enemyHold = HIGH_GROUND_HOLD_TICKS;
  if (fortress) world.enemyHold = Math.max(world.enemyHold, FORTRESS_HOLD_TICKS);

  const loadouts = cfg.loadouts ?? [];
  const heroLevel = clampLevel(cfg.heroLevel);

  // 좁은 협곡: 두 분대만 선다(`fieldedSquads`). 명부가 넷을 실어 와도 앞의 둘만 — 부관은 장수와 함께 선다.
  const fielded = canyon ? fieldedSquads(cfg.roster, cfg.companions, cfg.escortSquad ?? -1) : null;
  const roster = fielded ? cfg.roster.filter((e) => fielded.includes(e.squad ?? 0)) : cfg.roster;
  // 병사의 몸: 산길이면 말에서 내리고(`dismount`), 전황(비·바람)이 궁수에 걸리고(`weathered`), 중장보병이면 적 병사의
  // 방어가 오른다(`HEAVY_DEF_BONUS`, 적장은 그대로). 스펙에 거는 것은 나중에 나타나는 적(매복·원군)도 같은 몸이어야
  // 해서다 — 처음에는 중장보병을 배치한 뒤 배열(`sDef`)에 더해, 그 뒤에 선 적이 빠질 자리였다.
  const heavy = hasTrait(traits, Trait.HeavyInfantry);
  // 군량: 우리 병사의 체력이 오른다(`RATION_HP_PCT`). 명부로 서는 병사만 `shape`를 탄다 — 장수·동료는 그대로.
  const fed = hasSupply(supplies, Supply.Ration);
  // 특성 짝 장비: 분대마다 지휘관(0번은 장수, 그 뒤는 동료)의 착장이 지우는 날씨(`weatherproof`). 적은 없다.
  const proofOf = (squad: number): string[] => {
    // 명부의 착장은 믿지 않는다 — 칸에 맞는 아는 물건만(`sanitizeLoadout`).
    const l = sanitizeLoadout(loadouts[squad], cfg.playerCulture);
    if (!l) return [];
    const out: string[] = [];
    for (const id of Object.values(l)) for (const t of (typeof id === 'string' ? equipOf(id)?.weatherproof : undefined) ?? []) out.push(t);
    return out;
  };
  const shape = (team: Team) => (s: CombatantSpec, squad = 0): CombatantSpec => {
    const body = weathered(onFoot ? dismount(s) : s, team, traits, supplies, team === Team.Player ? proofOf(squad) : []);
    if (fed && team === Team.Player) return { ...body, stats: { ...body.stats, hp: Math.floor((body.stats.hp * RATION_HP_PCT) / 100) } };
    return heavy && team === Team.Enemy ? { ...body, stats: { ...body.stats, def: body.stats.def + HEAVY_DEF_BONUS } } : body;
  };
  const mates = cfg.companions ?? [];
  const pool = companionsOf(cfg.playerCulture);
  // 장수 곁의 동료(`escortSquad`). 리플레이 JSON은 아무 값이나 실어 오므로 1~3의 정수이고 그 자리에 아는 동료가
  // 있을 때만 받는다.
  // 같은 동료가 앞 분대에도 적힌 조작 명부면(배치는 앞 분대에만 세운다) 받지 않는다 — 받으면 그 동료가 앞 분대를
  // 지휘하면서 장수에게도 보정을 더해 두 번 셌다(리뷰가 짚었다).
  // 출전 위치가 장수 곁 분대를 알아야 해서 병사를 세우기 전에 정한다(난수를 쓰지 않는다).
  const esq = cfg.escortSquad;
  const escortDef =
    typeof esq === 'number' && Number.isInteger(esq) && esq >= 1 && esq < MAX_SQUADS && mates.indexOf(mates[esq]) === esq
      ? pool.find((c) => c.id === mates[esq])
      : undefined;
  const escortSquad = escortDef ? (esq as number) : -1;
  // 이 분대의 동료가 제 분대 지휘관으로 서는가(아래 동료 세우기와 같은 거르기).
  const leads = (sq: number): boolean => {
    const id = mates[sq];
    if (!id || sq === escortSquad || mates.indexOf(id) !== sq) return false;
    if (fielded && !fielded.includes(sq)) return false;
    return pool.some((c) => c.id === id);
  };
  const posts = cfg.deploy === undefined ? null : deployPosts(cfg.deploy, roster, leads, escortSquad, midX, spreadX);

  const firstMine = world.count;
  deploy(
    world,
    rng,
    roster,
    Team.Player,
    midX,
    playerY,
    spreadX,
    spreadY,
    1,
    shape(Team.Player),
    posts ? (sq) => posts.around(sq, playerY) : undefined,
  );
  // 화살 보급·방패 수리: 우리 병사에게만(장수·동료의 방패는 착장이라 그대로).
  const moreArrows = hasSupply(supplies, Supply.Arrows);
  const repaired = hasSupply(supplies, Supply.ShieldRepair);
  // 보급 끊김: 우리 궁수의 화살이 줄고(`SUPPLY_CUT_PCT`), 화살 보급은 그 뒤에 더한다 — 보급이 끊긴 판의 답이다.
  const cut = hasTrait(traits, Trait.SupplyCut);
  for (let i = firstMine; i < world.count; i++) {
    if (cut && world.branch[i] === BranchCode.Bow) world.arrows[i] = Math.floor((world.arrows[i] * SUPPLY_CUT_PCT) / 100);
    if (moreArrows && world.branch[i] === BranchCode.Bow) world.arrows[i] += ARROW_SUPPLY;
    if (repaired && world.shield[i] > 0) world.shield[i] = Math.floor((world.shield[i] * SHIELD_REPAIR_PCT) / 100);
  }
  if (hasSupply(supplies, Supply.Caltrops)) world.caltrops = CALTROP_TICKS;
  // 산길에서는 장수·동료도 말에서 내린다(`withoutMount`).
  const worn = (squad: number): Loadout | null | undefined => (onFoot ? withoutMount(loadouts[squad]) : loadouts[squad]);

  // 장수·동료·적장도 바위를 피해 세운다. 이들은 `deploy`를 거치지 않는다.
  // **적장이 바위에 박히면 그 판은 영영 안 끝난다** — 아무도 그를 죽일 수 없다.
  const spot = new Int32Array(2);

  // 장수의 장비는 직할대(0번)에 걸린다. 곁의 동료가 있으면 그 지휘 보정도 더한다(상한은 `World.setCaptain`).
  // 활을 든 영웅도 궁수라 날씨를 탄다(`weathered`) — 병사는 `shape`에서 거치지만 영웅은 여기서 바로 서므로 따로 건다.
  // 처음에는 빠져 있어 비 오는 밤에도 장수의 활만 맑은 날의 사거리였다(리뷰가 쟀다). 군량 같은 병사 전용 몫은 걸지 않는다.
  const heroWeather = (spec: CombatantSpec, squad: number): CombatantSpec => weathered(spec, Team.Player, traits, supplies, proofOf(squad));
  let leader = heroWeather(dressLeader(cfg.playerCulture, worn(0), heroLevel), 0);
  if (escortDef) {
    const lv = clampLevel(cfg.companionLevels?.[escortSquad] ?? 1);
    const extra = dressCompanion(escortDef, lv, worn(escortSquad), cfg.playerCulture).captain ?? NO_CAPTAIN;
    leader = { ...leader, captain: addCaptain(leader.captain ?? NO_CAPTAIN, extra) };
  }
  const [leadX, leadY] = posts ? posts.around(0, playerY) : [midX, playerY];
  world.freeSpot(leadX, leadY + fp(40), spot);
  world.spawn(leader, Team.Player, spot[0], spot[1], 0, 0);
  // 고른 칸을 대형의 자리로(장수에게서 본 상대 자리). 0번은 장수 자신이다.
  if (posts) for (let sq = 1; sq < MAX_SQUADS; sq++) if (posts.post[sq]) world.setPost(sq, posts.post[sq]![0], posts.post[sq]![1]);

  // 동료는 자기가 맡은 분대 한가운데에 선다. 색인이 곧 분대 번호다. 장수 곁의 동료는 0번 분대에 선다.
  // 같은 동료가 두 분대에 적힌 명부가 들어와도 한 번만(앞쪽 분대에) 세운다.
  // 화면에서는 나올 수 없는 조합이지만 리플레이는 아무 명부나 실어 올 수 있다.
  const placed = new Set<string>();
  for (let squad = 1; squad < MAX_SQUADS; squad++) {
    const id = mates[squad];
    if (!id || placed.has(id)) continue;
    if (fielded && squad !== escortSquad && !fielded.includes(squad)) continue;
    const def = pool.find((c) => c.id === id);
    if (!def) continue;
    // **병사가 없는 분대의 동료도 선다(단신 출전).** 처음에는 "혼자 나가 죽는다"며 세우지 않았는데, 그러면
    // 병사를 다 잃은 분대의 동료는 출정 내내 구경만 했고, 빈 분대에 앉힌 동료는 명단에만 있고 전장에 없어
    // 경험치 몫이 꼬였다(`earningCompanions`). 플레이한 사람이 청했다: "병사가 없어도 동료장수를 출전시켜줘."
    // 혼자 선 동료도 분대 지휘관이라 분대 명령(공격·대형·경로)을 그대로 받는다.
    // **NaN을 막는다.** 리플레이 JSON은 아무 값이나 실어 올 수 있고, NaN은
    // `Math.floor`도 `Math.min/max`도 그대로 통과해 `setCaptain`까지 간다.
    // 거기서 Int16Array에 들어가며 0이 되어 그 분대의 타격이 통째로 0이 된다.
    // 조용히 싸움이 달라지는 자리라 문 앞에서 막는다.
    const level = clampLevel(cfg.companionLevels?.[squad] ?? 1);
    const [cmx, cmy] = posts ? posts.around(squad === escortSquad ? 0 : squad, playerY) : [midX, playerY];
    world.freeSpot(cmx, cmy, spot);
    // 장수 곁의 동료는 0번 분대에 선다. 0번의 동료는 지휘하지 않는다(`World.spawn`) — 제 분대는 지휘관 없이 선다.
    const stands = squad === escortSquad ? 0 : squad;
    const who = world.spawn(
      // 날씨를 지우는 장비는 제 분대의 지휘관 것이다(`proofOf`) — 장수 곁에 선 동료도 제 착장으로 본다.
      heroWeather(underCommand(dressCompanion(def, level, worn(squad), cfg.playerCulture), heroLevel), squad),
      Team.Player,
      spot[0],
      spot[1],
      0,
      stands,
    );
    // 고유 기술(`mate-skills.ts`): 선 분대에 건다 — 장수 곁의 동료는 내 부대에. 레벨은 동료 레벨에서 나온다.
    const ms = MATE_SKILL_BY_ID.get(def.id);
    if (ms && who >= 0) {
      const pcts = mateSkillPcts(ms, mateSkillLevel(level));
      world.setMateSkill(stands, who, ms.trigger, pcts.atk, pcts.cut, ms.rally, ms.ticks, ms.cd, fp(pcts.radius));
    }
    placed.add(id);
  }

  // 매복: 처음 편성의 `AMBUSH_PCT`%가 더, `AMBUSH_TICKS` 뒤 우리 전열 뒤(아래) 양끝에서 반씩 나타난다. 원군:
  // `REINFORCE_EVERY`마다 처음 편성의 `REINFORCE_PCT`%가 적의 뒤(위쪽)에서 온다.
  const foes = enemyRoster(cfg);
  // 횡대로 오는 적은 좁게 선다(`FOE_LINE_X`). 토벌은 옛 폭 그대로다.
  let foeCount = 0;
  for (const e of foes) foeCount += e.count;
  const foeSpreadX = cfg.raid ? spreadX : Math.min(spreadX, Math.max(FOE_LINE_X, foeCount * FOE_LINE_PER_SOLDIER));
  let uid = deploy(world, rng, foes, Team.Enemy, midX, enemyY, foeSpreadX, spreadY, 0, shape(Team.Enemy));
  const shapeFoe = shape(Team.Enemy);
  if (hasTrait(traits, Trait.Ambush)) {
    for (const e of ambushBand(foes)) {
      const spec = shapeFoe(boostSpec(specForTroop(e.troop, clampLevel(e.level), false), e.boost));
      const left = Math.ceil(e.count / 2);
      for (const [count, x] of [[left, fp(80)], [e.count - left, wFp - fp(80)]] as const) {
        if (count <= 0) continue;
        world.queueSpawn({ tick: AMBUSH_TICKS, spec, team: Team.Enemy, count, x, y: hFp - fp(60), spread: fp(40), uidBase: uid });
        uid += count;
      }
    }
  }
  if (hasTrait(traits, Trait.Reinforce)) {
    const wave = reinforceWave(foes);
    for (let w = 1; w <= REINFORCE_WAVES; w++) {
      for (const e of wave) {
        const spec = shapeFoe(boostSpec(specForTroop(e.troop, clampLevel(e.level), false), e.boost));
        world.queueSpawn({ tick: w * REINFORCE_EVERY, spec, team: Team.Enemy, count: e.count, x: midX, y: fp(80), spread: spreadX >> 1, uidBase: uid });
        uid += e.count;
      }
    }
  }
  // 적장의 레벨은 깊이를 따른다(`foeChiefLevel`): 깊이 30까지는 예전 그대로, 그 너머는 병사의 기본 레벨과 같이 99까지.
  // 적 명부(`foes` — 깊이·나라·특성에서 다시 만든다, 클라이언트의 명부가 아니다)에 단련이 실려 있으면 적장도 같이
  // 단련된다. 113부터 `depthRoster`가 싣지 않으므로 지금은 늘 그대로다 — 단련을 되살리는 날 적장만 빠지지 않게 둔다.
  const rider = cfg.raid ? banditChiefSpec(cfg.raidBand) : boostSpec(chiefSpec(cfg.enemyCulture, foeChiefLevel(cfg.depth)), foes[0]?.boost);
  const chief = onFoot ? dismount(rider) : rider;
  // 적장 결전: 적장이 여럿(`WARLORDS`), 가운데와 양옆. 마지막이 쓰러지면 적이 모두 무너진다(`World.warlords`).
  // 결사대: 적 병사가 무너지지 않는다(`World.diehard`).
  if (hasTrait(traits, Trait.Diehard)) world.diehard = true;
  // 적장은 제 병사들과 함께 온다(`CHIEF_CHARGE`). 토벌의 두령은 예전처럼 제 걸음이다.
  world.chiefMarch = !cfg.raid;
  // 적 병사는 횡대로 온다(`LINE_RELEASE`). 토벌은 예전대로 — 장수 혼자라 졸개가 한꺼번에 닿으면 다른 싸움이 된다.
  world.lineMarch = !cfg.raid;
  // **적의 부대**(`FOE_SQUADS`): 처음 선 적 병사를 가로로 줄 세워 부대로 나누고, 부대마다 대장을 세운다. 가운데 부대가
  // 0번(총대장의 부대)이고 나머지는 부장이 맡는다. 적장 결전은 적장 셋이 세 부대의 대장이다. 토벌에는 부대가 없다.
  // 뒤에 서는 적(매복·원군)은 0번 부대다(`World.foeSquad`의 기본값).
  const foeMen: number[] = [];
  for (let i = 0; i < world.count; i++) if (world.team[i] === Team.Enemy && world.role[i] === Role.Soldier) foeMen.push(i);
  // 옆 자리 순서(같으면 색인 순) — 부대는 가로로 이웃한 병사들의 묶음이다.
  foeMen.sort((a, b) => world.x[a] - world.x[b] || a - b);
  const warlords = hasTrait(traits, Trait.Warlords);
  // 부대 수는 명부와 같은 입력에서 나온다(`foeSquadsAt` — 부장의 값을 치른 그 수다). 병사보다 많을 수는 없다.
  const squadsN = cfg.raid ? 1 : Math.max(1, Math.min(foeMen.length, foeSquadsAt(cfg.enemyCulture, cfg.depth, traits)));
  const mid = squadsN >> 1;
  /**
   * 왼쪽부터 `g`번째 묶음의 부대 번호: 가운데가 0, 나머지는 왼쪽부터 1·2·…. **적장 결전은 가운데 셋이 0·1·2번이다**
   * (가운데 0, 그 왼쪽 이웃 1, 오른쪽 이웃 2 — 적장의 부대, `foeHeadSquad`) 나머지가 왼쪽부터 3·4·…(부장의 부대).
   * 번호가 곧 이름(`foeLeader`)과 직함이라 자리·이름·적장 여부가 이 한 곳에서 나온다. 부대가 셋이면 두 규칙이 같다.
   */
  const squadOf = (g: number): number => {
    if (g === mid) return 0;
    if (!warlords) return g < mid ? g + 1 : g;
    if (g === mid - 1) return 1;
    if (g === mid + 1) return 2;
    // 가운데 셋을 뺀 나머지: 왼쪽 묶음들이 3부터, 이어서 오른쪽 묶음들.
    return g < mid ? WARLORDS + g : WARLORDS + (mid - 1) + (g - mid - 2);
  };
  const groupX = new Array<number>(squadsN).fill(0);
  const groupN = new Array<number>(squadsN).fill(0);
  for (let k = 0; k < foeMen.length; k++) {
    const g = Math.min(squadsN - 1, Math.floor((k * squadsN) / foeMen.length));
    world.setFoeSquad(foeMen[k], squadOf(g));
    groupX[g] += Math.floor(world.x[foeMen[k]] / FP_ONE);
    groupN[g]++;
  }
  const groupAt = (g: number): number => (groupN[g] > 0 ? fp(Math.floor(groupX[g] / groupN[g])) : midX);
  // **적의 용사**(`FOE_ELITE`): 부대마다 정한 수만큼(`foeElitePlan`), 그 부대에서 가장 센 근접 병사부터(티어 → 격 → 색인).
  // 부대가 정해진 뒤에야 고를 수 있어 선 사람의 몸을 올린다(`World.promoteElite`). 117 전에는 병사를 세울 때 명부의 가장
  // 센 줄의 앞사람을 용사로 세웠다 — 어느 부대에 들지 모르는 채였고 모두 총대장을 지켰다. 토벌에는 없다.
  if (!cfg.raid) {
    const plan = foeElitePlan(foeEliteCount(cfg.depth), squadsN);
    const tierOf = (i: number): number => world.specs[world.specIdx[i]].tier;
    for (let k = 0; k < squadsN; k++) {
      if (plan[k] <= 0) continue;
      const melee = foeMen.filter((i) => world.foeSquad[i] === k && world.branch[i] !== BranchCode.Bow);
      melee.sort((a, b) => tierOf(b) - tierOf(a) || world.sPower[b] - world.sPower[a] || a - b);
      for (let n = 0; n < plan[k] && n < melee.length; n++) world.promoteElite(melee[n], foeEliteSpec(world.specs[world.specIdx[melee[n]]]));
    }
  }
  if (warlords) world.warlords = true;
  if (!cfg.raid) {
    const sub = onFoot ? dismount(subChiefSpec(cfg.enemyCulture, foeChiefLevel(cfg.depth))) : subChiefSpec(cfg.enemyCulture, foeChiefLevel(cfg.depth));
    for (let g = 0; g < squadsN; g++) {
      if (g === mid) continue;
      // 대장은 제 부대의 뒤(위)에 선다. 적장 결전의 옆 적장(1·2번 부대)은 총대장의 몸이고 나머지는 부장이다 — 117까지는
      // 결전의 옆 적장이 가운데에서 160px 떨어진 정해진 자리에 섰다(부대가 셋뿐이었다).
      const k = squadOf(g);
      world.freeSpot(groupAt(g), enemyY - fp(30), spot);
      world.setFoeSquad(world.spawn(foeHeadSquad(k, warlords) ? chief : sub, Team.Enemy, spot[0], spot[1], k), k);
    }
  }
  // 총대장은 마지막에 선다(`World.chiefIdx`가 가리키는 사람이다).
  world.freeSpot(midX, enemyY - fp(40), spot);
  world.setFoeSquad(world.spawn(chief, Team.Enemy, spot[0], spot[1], 0), 0);

  // 호위할 사람은 모두 선 뒤에 찾는다(`World.bindDuties`).
  world.bindDuties();
  return world;
}

export function defaultConfig(seed: number, depth = 1): ScenarioConfig {
  return {
    seed,
    cols: FIELD_COLS,
    rows: FIELD_ROWS,
    depth,
    playerCulture: Culture.Goguryeo,
    enemyCulture: Culture.Silla,
    roster: standardRoster(Culture.Goguryeo),
    // 표준 부대와 같은 만큼 싸워 온 장수다.
    heroLevel: STANDARD_LEVEL,
    // 0번은 장수 자리라 비워 둔다.
    companions: [null, ...companionsOf(Culture.Goguryeo).map((c) => c.id)],
    // 출전 위치는 기본 배치(`autoDeploy`) — 게임이 늘 싣는 것과 같게. 비우면 옛 방식(섞여 선다)이라 도구가 재는 판이
    // 실제 판과 갈린다.
    deploy: [],
  };
}
